import React, { useEffect, useMemo, useState } from 'react';
import { X, Edit3, Check, Eye, EyeOff, Link2, ExternalLink } from 'lucide-react';
import { Scenario } from '../../types/app';
import classNames from 'classnames';
import { SCENARIO_COLORS } from '../../utils/constants';

interface ScenarioCardProps {
  scenario: Scenario;
  isActive: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onTitleChange: (title: string) => void;
  onLinkChange: (link?: string) => void;
  onToggleVisibility: () => void;
  onToggleBaseline: () => void;
  colorIndex?: number;
}

export const ScenarioCard: React.FC<ScenarioCardProps> = ({
  scenario,
  isActive,
  onSelect,
  onRemove,
  onTitleChange,
  onLinkChange,
  onToggleVisibility,
  onToggleBaseline,
  colorIndex,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(scenario.title);
  const [isLinkEditing, setIsLinkEditing] = useState(false);
  const [editLink, setEditLink] = useState(scenario.link || '');

  useEffect(() => {
    if (!isLinkEditing) {
      setEditLink(scenario.link || '');
    }
  }, [scenario.link, isLinkEditing]);
  
  const linkLabel = useMemo(() => {
    if (!scenario.link) return '';
    try {
      const url = new URL(scenario.link);
      return url.hostname.replace(/^www\./i, '') || url.toString();
    } catch {
      return scenario.link;
    }
  }, [scenario.link]);

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditTitle(scenario.title);
  };

  const handleSaveEdit = (e?: React.MouseEvent | React.FocusEvent) => {
    e?.stopPropagation();
    setIsEditing(false);
    if (editTitle.trim()) {
      onTitleChange(editTitle.trim());
    } else {
      setEditTitle(scenario.title);
    }
  };

  const handleCancelEdit = (e?: React.KeyboardEvent) => {
    if (e?.key === 'Escape') {
      setIsEditing(false);
      setEditTitle(scenario.title);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    }
    handleCancelEdit(e);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove();
  };

  const handleToggleVisibility = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleVisibility();
  };

  const handleToggleBaseline = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleBaseline();
  };

  const handleStartLinkEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLinkEditing(true);
    setEditLink(scenario.link || '');
  };

  const handleSaveLink = (e?: React.MouseEvent | React.FocusEvent | React.KeyboardEvent) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    const trimmed = editLink.trim();
    let normalized = trimmed;
    if (trimmed && !/^https?:\/\//i.test(trimmed)) {
      normalized = `https://${trimmed}`;
    }
    onLinkChange(normalized || undefined);
    setIsLinkEditing(false);
    setEditLink(normalized || '');
  };

  const handleCancelLinkEdit = (e?: React.MouseEvent | React.KeyboardEvent) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setIsLinkEditing(false);
    setEditLink(scenario.link || '');
  };

  const handleLinkKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveLink(e);
    }
    if (e.key === 'Escape') {
      handleCancelLinkEdit(e);
    }
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation();
  };

  const cardClass = classNames(
    'p-3 rounded-lg border-2 cursor-pointer transition-all duration-200',
    {
      'border-blue-500 bg-blue-50 shadow-md': isActive,
      'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm': !isActive,
      'animate-pulse': scenario.isAnalyzing && !scenario.analysisResult,
    }
  );

  // Color swatch derived from provided index or fallback to id-hash
  let computedIndex = typeof colorIndex === 'number' ? colorIndex : (parseInt(scenario.id, 36) % SCENARIO_COLORS.length + SCENARIO_COLORS.length) % SCENARIO_COLORS.length;
  const color = SCENARIO_COLORS[computedIndex % SCENARIO_COLORS.length];

  return (
    <div className={cardClass} onClick={onSelect}>
      <div className="flex items-center justify-between mb-1">
        {isEditing ? (
          <div className="flex items-center space-x-1 flex-1">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              className="text-sm font-medium text-gray-900 bg-transparent border-b border-gray-300 focus:border-blue-500 focus:outline-none flex-1"
              autoFocus
              onBlur={handleSaveEdit}
            />
            <button
              onClick={handleSaveEdit}
              className="p-1 text-green-600 hover:bg-green-100 rounded"
            >
              <Check className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="flex items-center space-x-2 flex-1">
            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
            <h3 className="text-sm font-medium text-gray-900 leading-tight break-words">
              {scenario.title}
            </h3>
            <button
              onClick={handleStartEdit}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Edit3 className="w-3 h-3" />
            </button>
          </div>
        )}

        <div className="flex items-center space-x-1">
          {scenario.scenarioType === 'buying' && (
            <button
              onClick={handleToggleBaseline}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                scenario.isBaseline
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
              title={scenario.isBaseline ? 'Currently baseline' : 'Set as baseline'}
            >
              {scenario.isBaseline ? 'Baseline' : 'Set Baseline'}
            </button>
          )}
          <button
            onClick={handleToggleVisibility}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            title={scenario.visible !== false ? "Hide from chart" : "Show in chart"}
          >
            {scenario.visible !== false ? (
              <Eye className="w-4 h-4" />
            ) : (
              <EyeOff className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={handleRemove}
            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-100 rounded"
            title="Remove scenario"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Compact info row */}
      <div className="flex items-center justify-between text-xs text-gray-600 mt-1">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          scenario.scenarioType === 'buying'
            ? 'bg-green-100 text-green-800'
            : 'bg-blue-100 text-blue-800'
        }`}>
          {scenario.scenarioType === 'buying' ? 'Buying' : 'Renting'}
        </span>
        <div className="flex items-center space-x-1">
          {isLinkEditing ? (
            <div
              className="flex items-center space-x-1"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="text"
                value={editLink}
                onChange={(e) => setEditLink(e.target.value)}
                onKeyDown={handleLinkKeyDown}
                autoFocus
                className="w-32 px-1 py-0.5 border-b border-gray-300 text-xs text-gray-700 bg-transparent focus:outline-none focus:border-blue-500"
                placeholder="https://..."
              />
              <button
                onClick={handleSaveLink}
                className="p-1 text-green-600 hover:bg-green-100 rounded"
                title="Save link"
              >
                <Check className="w-3 h-3" />
              </button>
              <button
                onClick={handleCancelLinkEdit}
                className="p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 rounded"
                title="Cancel"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : scenario.link ? (
            <>
              <a
                href={scenario.link}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleLinkClick}
                className="flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium text-blue-600 hover:bg-blue-50"
                title={scenario.link}
              >
                <ExternalLink className="w-3 h-3" />
                <span>{linkLabel || 'Open link'}</span>
              </a>
              <button
                onClick={handleStartLinkEdit}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                title="Edit link"
              >
                <Link2 className="w-3 h-3" />
              </button>
            </>
          ) : (
            <button
              onClick={handleStartLinkEdit}
              className="flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              title="Attach a link to this scenario"
            >
              <Link2 className="w-3 h-3" />
              <span>Attach link</span>
            </button>
          )}
        </div>
      </div>

      {scenario.isAnalyzing && !scenario.analysisResult && (
        <div className="text-blue-600 text-xs mt-1">Analyzing...</div>
      )}

      {scenario.error && (
        <div className="text-red-600 text-xs truncate mt-1" title={scenario.error}>
          Error: {scenario.error}
        </div>
      )}
    </div>
  );
};
